import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import AnimatedSection from "@/components/AnimatedSection";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Clock, Users, BarChart3 } from "lucide-react";

const situations = [
  "Merger investigations and challenges",
  "Government subpoenas or civil investigative demands",
  "Antitrust litigation or competitor disputes",
  "Urgent questions involving pricing, distribution, or exclusivity practices",
];

const HighStakes = () => (
  <Layout>
    <PageHead title="High-Stakes Antitrust Matters" description="Alicia J. Batts provides rapid strategic assessment for merger investigations, government subpoenas, antitrust litigation, and urgent competition issues." path="/high-stakes" keywords="Alicia Batts high stakes antitrust, urgent antitrust counsel, merger investigation response, government subpoena antitrust, Batts Legal" />
    <section className="bg-hero text-primary-foreground py-20">
      <div className="editorial-container">
        <AnimatedSection animation="fade"><div className="gold-accent-line mb-6" /></AnimatedSection>
        <AnimatedSection animation="fade-up" delay={100}>
          <h1 className="text-4xl md:text-5xl font-serif font-semibold mb-4">High-Stakes Antitrust Matters</h1>
        </AnimatedSection>
        <AnimatedSection animation="fade-up" delay={200}>
          <p className="text-lg opacity-80 max-w-2xl">When critical competition issues arise, immediate strategic counsel makes the difference.</p>
        </AnimatedSection>
      </div>
    </section>

    <section className="section-padding">
      <div className="editorial-container">
        <div className="grid lg:grid-cols-2 gap-16">
          <AnimatedSection animation="fade-left">
            <div>
              <h2 className="font-serif text-3xl font-semibold text-foreground mb-6">When Companies Face Urgent Competition Issues</h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                High-stakes antitrust matters demand rapid strategic assessment, deep substantive expertise, and coordination with economists and industry experts. Batts Legal LLP provides the counsel corporations need in critical moments.
              </p>
              <ul className="space-y-4">
                {situations.map((s) => (
                  <li key={s} className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                    <span className="text-foreground">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedSection>
          <div className="space-y-6">
            {[
              { icon: Clock, title: "Rapid Assessment", desc: "Immediate strategic evaluation of the competitive landscape and enforcement risks." },
              { icon: Users, title: "Expert Coordination", desc: "Seamless collaboration with economists, industry consultants, and co-counsel as needed." },
              { icon: BarChart3, title: "Strategic Positioning", desc: "Developing the competitive narrative and advocacy strategy from day one." },
            ].map((item, i) => (
              <AnimatedSection key={item.title} animation="fade-right" delay={i * 120}>
                <div className="bg-card rounded-md p-6 shadow-card border border-border">
                  <item.icon className="w-6 h-6 text-gold mb-3" />
                  <h3 className="font-serif text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
        <AnimatedSection animation="fade-up" delay={200}>
          <div className="mt-16 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-sm font-medium text-sm hover:opacity-90 transition-opacity"
            >
              Contact Us Immediately <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>
  </Layout>
);

export default HighStakes;
